export const parseProseMirrorSchema = (data: string | null | undefined) => {
  if (data === null || data === undefined) {
    return "";
  }

  let result = "";

  try {
    if (JSON.parse(data).content === undefined) {
      return "";
    }

    result = JSON.parse(data).content.reduce((acc: string, item: any) => {
      if (!item.content) {
        acc += "\n";
        return acc;
      }

      const reduced = item.content?.reduce(
        (
          accInner: string,
          itemInner: any,
          indexInner: number,
          arrInner: any
        ) => {
          let newChars = "";
          if (itemInner.type === "text" && itemInner.text.length > 0) {
            if (indexInner + 1 === arrInner.length) {
              if (
                Array.isArray(itemInner.marks) &&
                itemInner.marks.some((x: any) => x.type === "link")
              ) {
                newChars = `${itemInner.text}`;
              } else {
                newChars = itemInner.text;
              }
            } else {
              newChars = `${itemInner.text}`;
            }
          }

          if (itemInner.type === "listItem" && itemInner.content?.length > 0) {
            newChars =
              "\n" +
              itemInner.content
                .map((x: any) => {
                  if (x.type === "paragraph" && !!x.content) {
                    return x.content.map((y: any) => {
                      return y.text ? `${y.text}` : "";
                    });
                  }
                  return "";
                })
                .join("");
          }
          accInner += newChars;
          return accInner;
        },
        ""
      );

      acc += reduced;
      return acc;
    }, "");
  } catch (_) {
    result = data;
  }
  return result;
};

export const getProseMirrorMarkers = (data: string) => {
  let proseMirror = [];
  try {
    proseMirror = JSON.parse(data) || [];
  } catch (e) {
    proseMirror = [];
  }

  const result: string[] = [];

  const handleLevel = (level: Record<string, any>[]) => {
    level?.forEach((item) => {
      if (item.type === "text" && item.marks?.length > 0) {
        item.marks.forEach((mark: any) => {
          if (mark.type === "marker" && mark.attrs?.id) {
            result.push(mark.attrs.id);
          }
        });
      }
      if (item.content) {
        handleLevel(item.content);
      }
    });
  };

  handleLevel(proseMirror.content);

  return result;
};

/**
 * Extracts text from ProseMirror JSON schema by position indices
 * @param data - ProseMirror JSON string
 * @param from - Start position (inclusive)
 * @param to - End position (exclusive)
 * @returns Extracted text fragment
 */
export const extractProseMirrorTextByIndices = (
  data: string | null | undefined,
  from: number,
  to: number
): string => {
  if (!data || from < 0 || to < from) {
    return "";
  }

  let parsedContent;
  try {
    parsedContent = JSON.parse(data);
    if (!parsedContent?.content) {
      return "";
    }
  } catch (_) {
    // If not valid JSON, treat as plain text
    const plainText = String(data);
    return plainText.substring(
      Math.max(0, from),
      Math.min(plainText.length, to)
    );
  }

  let currentPos = 0;
  let result = "";

  const extractFromContent = (content: any[]): void => {
    for (const item of content) {
      if (currentPos >= to) {
        return; // We've reached the end position
      }

      if (item.type === "text" && item.text) {
        const textLength = item.text.length;
        const textStart = currentPos;
        const textEnd = currentPos + textLength;

        // Check if this text node overlaps with our target range
        if (textEnd > from && textStart < to) {
          // Calculate the portion of this text we need
          const startOffset = Math.max(0, from - textStart);
          const endOffset = Math.min(textLength, to - textStart);
          result += item.text.substring(startOffset, endOffset);
        }

        currentPos = textEnd;
      } else if (item.type === "paragraph") {
        // Paragraphs don't add to position, but their content does
        if (item.content) {
          extractFromContent(item.content);
        }
        // Add newline after paragraph if we're in the range
        if (currentPos > from && currentPos <= to) {
          result += "\n";
        }
      } else if (item.type === "listItem") {
        if (item.content) {
          extractFromContent(item.content);
        }
      } else if (item.type === "bulletList" || item.type === "orderedList") {
        if (item.content) {
          extractFromContent(item.content);
        }
      } else if (item.content) {
        extractFromContent(item.content);
      }
    }
  };

  extractFromContent(parsedContent.content);

  return result.trim();
};

/**
 * Converts ProseMirror document positions to plain text positions
 * Uses the same logic as parseProseMirrorSchema to accumulate positions
 * ProseMirror positions are between characters, plain text positions are character indices
 * @param data - ProseMirror JSON string
 * @param proseMirrorPos - Position in ProseMirror document (between characters)
 * @returns Position in plain text (character index)
 */
export const convertProseMirrorPosToPlainText = (
  data: string | null | undefined,
  proseMirrorPos: number
): number => {
  if (!data || proseMirrorPos < 0) {
    return proseMirrorPos;
  }

  let parsedContent;
  try {
    parsedContent = JSON.parse(data);
    if (!parsedContent?.content) {
      return proseMirrorPos;
    }
  } catch (_) {
    // If not valid JSON, treat as plain text - positions are the same
    return proseMirrorPos;
  }

  // Use extractProseMirrorTextByIndices to find the plain text position
  // by extracting text up to the ProseMirror position
  const textUpToPos = extractProseMirrorTextByIndices(data, 0, proseMirrorPos);
  return textUpToPos.length;
};
